/**
 * Company card for the explorer grid
 */

import React from 'react'
import { ExternalLink } from 'lucide-react'
import type { Company } from './types'

interface CompanyCardProps {
  company: Company
  onClick: () => void
}

export const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  onClick,
}) => {
  const year = company.launched_at
    ? new Date(company.launched_at).getFullYear()
    : null

  const formatDomain = (domain: string) => {
    return domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  return (
    <div
      onClick={onClick}
      className="bg-secondary hover:bg-tertiary transition-colors rounded-lg p-4 cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        {/* Logo */}
        <div className="w-12 h-12 flex-shrink-0 bg-tertiary rounded-lg overflow-hidden flex items-center justify-center">
          {company.logo ? (
            <img
              src={company.logo}
              alt={company.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          ) : (
            <span className="text-lg font-bold text-accent">
              {company.name.charAt(0)}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-primary truncate group-hover:text-accent transition-colors">
              {company.name}
            </h3>
            <span className="text-xs text-secondary flex-shrink-0">
              {company.batch}
            </span>
          </div>

          {/* One-liner */}
          <p className="text-sm text-secondary mt-1 line-clamp-2">
            {company.short.split('\n')[1] || company.short}
          </p>

          {/* Domain tags */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 bg-accent/20 text-accent rounded">
              {formatDomain(company.domain)}
            </span>
            {company.is_agent && (
              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-600 dark:text-green-400 rounded">
                AI Agent
              </span>
            )}
            {year && (
              <span className="text-xs text-secondary">{year}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyCard
